import { useContext, useState } from 'react';

import { Card, Col, Text } from '@gympass/yoga';
import { CornerUpRight } from '@gympass/yoga-icons';
import { useFlag } from '@unleash/proxy-client-react';
import { isLegacyEndUserOnboardingAtom } from 'atoms/isLegacyEndUserOnboardingAtom';
import withAuth from 'hocs/withAuth/withAuth';
import { useSetAtom } from 'jotai';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { FeatureToggle } from 'utils/unleash/toggles';

import { SpecialistContext, useModals, usePermissionConfig } from 'context';
import {
  getSpecialistRole,
  getSpecialistType,
} from 'context/SpecialistContext/SpecialistContext';
import { useOnInit, useRequest } from 'hooks';
import MinusCircle from 'public/assets/icons/minus-circle.svg';
import { EndUserSessionsState, EndUserState } from 'services';
import {
  Discharge,
  SpecialistRole,
  updateEndUserDischarge,
} from 'services/specialist';
import { getEndUserInfo, getEndUserSessions } from 'services/user';

import {
  CriticalUserSection,
  HeaderUserInfo,
  NotesSection,
  PatientRecords,
  SessionsTable,
  TabList,
  Tooltip,
} from 'components';
import { DischargeComponentProps, FeedbackComponentProps } from 'modals';
import { DocDownloadComponentProps } from 'modals/DocDownload/types';
import { ForwardComponentProps } from 'modals/Forward';

import * as S from './styles';

const EndUser: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { id = '' } = useParams<{
    id: string;
  }>();

  const { specialistInfo } = useContext(SpecialistContext);

  const isPatientNotesEnabled = useFlag(FeatureToggle.PatientRecords);

  const [userInfo, setUserInfo] = useState<EndUserState>();
  const [sessionsList, setSessionsList] = useState<EndUserSessionsState[]>([]);
  const getUserInfoRequest = useRequest();
  const getUserSessionsRequest = useRequest();
  const updateDischargeRequest = useRequest();
  const { openModal, closeModal } = useModals();
  const { hasAccess } = usePermissionConfig();
  const setUserIsLegacy = useSetAtom(isLegacyEndUserOnboardingAtom);

  const specialistRole = getSpecialistRole(specialistInfo?.groups);
  const specialistType = getSpecialistType(specialistInfo?.groups);

  const getUserInfo = async () => {
    getUserInfoRequest.setLoading();
    try {
      const { data } = await getEndUserInfo(id);
      setUserIsLegacy(!data.care_profile);
      setUserInfo(data);
      getUserInfoRequest.setSuccess();
    } catch (e) {
      getUserInfoRequest.setError(e);
    }
  };

  const getUserSessions = async () => {
    if (!hasAccess('session')) return;
    getUserInfoRequest.setLoading();
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { data: dataSessions } = await getEndUserSessions(timezone, id);
      setSessionsList(
        dataSessions.map((it, idx): EndUserSessionsState => {
          const item = { ...it };
          item.session = idx + 1;
          return item;
        }),
      );
      getUserSessionsRequest.setSuccess();
    } catch (e) {
      getUserInfoRequest.setError(e);
    }
  };

  const handleEndUserDischarge = async (discharge: Discharge) => {
    updateDischargeRequest.setLoading();
    try {
      await updateEndUserDischarge(id, discharge);

      updateDischargeRequest.setSuccess();
      openModal<FeedbackComponentProps>('feedback', {
        props: {
          title: 'DISCHARGE.MODAL.SUCCESS',
          status: 'success',
        },
        onClose: () => {
          closeModal('discharge');
          navigate(`/care-provider/patient-list`);
        },
      });
    } catch (e) {
      const fallbackMessage = 'Error while updating discharge request';
      updateDischargeRequest.setError(e, fallbackMessage);
      openModal<FeedbackComponentProps>('feedback', {
        props: {
          title: updateDischargeRequest.errorMessage() || fallbackMessage,
          status: 'error',
        },
      });
    }
  };

  const initiateDischargeFlow = () => {
    if (isPatientNotesEnabled) {
      openModal<DocDownloadComponentProps>('docDownload', {
        props: {
          cognitoId: id,
          onNext: () => {
            closeModal('docDownload');
            openModal<DischargeComponentProps>('discharge', {
              props: {
                onSubmit: (discharge: Discharge) =>
                  handleEndUserDischarge(discharge),
              },
            });
          },
          showDischargeInfo: true,
        },
      });
    } else {
      openModal<DischargeComponentProps>('discharge', {
        props: {
          onSubmit: (discharge: Discharge) => handleEndUserDischarge(discharge),
        },
      });
    }
  };

  const initiateForwardFlow = () => {
    if (userInfo) {
      openModal<ForwardComponentProps>('forward', {
        props: {
          endUser: userInfo,
        },
      });
    }
  };

  useOnInit(() => {
    getUserInfo();
    getUserSessions();
  });

  const BasicInfo = () => {
    return (
      <S.BasicInfoContainer data-testid="basicInfo">
        <Card.Content>
          <Card.Header>
            <S.HeaderContent>
              <Text.H5 data-testid="basicInfoHeaderTitle">
                <FormattedMessage id="ENDUSER.INFO" />
              </Text.H5>
              {specialistRole === SpecialistRole.CP &&
                (userInfo?.is_critical &&
                !userInfo?.criticality?.is_criticality_confirmed ? (
                  <Tooltip
                    position="top-center"
                    description="ENDUSER.DISCHARGE.TOOLTIP.DISABLED"
                  >
                    <S.DischargeButton
                      small
                      data-testid="basicInfoHeaderDischargeButton"
                      onClick={initiateDischargeFlow}
                      disabled
                    >
                      <>
                        <MinusCircle />
                        <FormattedMessage id="ENDUSER.DISCHARGE" />
                      </>
                    </S.DischargeButton>
                  </Tooltip>
                ) : (
                  <S.DischargeButton
                    small
                    data-testid="basicInfoHeaderDischargeButton"
                    onClick={initiateDischargeFlow}
                  >
                    <>
                      <MinusCircle />
                      <FormattedMessage id="ENDUSER.DISCHARGE" />
                    </>
                  </S.DischargeButton>
                ))}
              {specialistRole === SpecialistRole.TC && (
                <S.DischargeButton
                  small
                  data-testid="basicInfoHeaderForwardButton"
                  onClick={initiateForwardFlow}
                >
                  <>
                    <CornerUpRight />
                    <FormattedMessage id="ENDUSER.FORWARD" />
                  </>
                </S.DischargeButton>
              )}
            </S.HeaderContent>
          </Card.Header>
          <S.InputsContainer fluid>
            <S.EndUserAvatar>
              <S.EndUserAvatarText>
                {userInfo?.fullName
                  .split(' ')
                  .slice(0, 2)
                  .map(word => word[0])
                  .join(',')
                  .replace(',', '')}
              </S.EndUserAvatarText>
            </S.EndUserAvatar>
            <S.ContainerRows>
              <S.RowsInfo>
                <Col xxs={6}>
                  <S.InputInfo
                    data-testid="inputFullName"
                    full
                    disabled
                    cleanable={false}
                    label={intl.formatMessage({ id: 'ENDUSER.INFO.NAME' })}
                    value={userInfo?.fullName}
                  />
                </Col>
                <Col xxs={6}>
                  <S.InputInfo
                    full
                    disabled
                    cleanable={false}
                    label={intl.formatMessage({ id: 'ENDUSER.INFO.EMAIL' })}
                    value={userInfo?.email}
                  />
                </Col>
              </S.RowsInfo>
              <S.RowsInfo>
                <Col xxs={4}>
                  <S.InputInfo
                    type="date"
                    full
                    disabled
                    cleanable={false}
                    label={intl.formatMessage({ id: 'ENDUSER.INFO.DATE' })}
                    value={userInfo?.birthdate.split('/').reverse().join('-')}
                  />
                </Col>
                <Col xxs={4}>
                  <S.InputInfo
                    full
                    disabled
                    cleanable={false}
                    label={intl.formatMessage({ id: 'ENDUSER.INFO.GENDER' })}
                    value={userInfo?.gender}
                  />
                </Col>
                <Col xxs={4}>
                  <S.InputInfo
                    full
                    disabled
                    cleanable={false}
                    label={intl.formatMessage({ id: 'ENDUSER.INFO.PHONE' })}
                    value={userInfo?.phoneNumber}
                  />
                </Col>
              </S.RowsInfo>
            </S.ContainerRows>
          </S.InputsContainer>
        </Card.Content>
      </S.BasicInfoContainer>
    );
  };

  const SessionsInfo = () => {
    if (!hasAccess('session') || sessionsList?.length <= 0) return <></>;
    return (
      <S.SessionsSection>
        <Card.Header>
          <Text.H5 data-testid="sessionsTitle">
            <FormattedMessage id="ENDUSER.SESSION" />
          </Text.H5>
        </Card.Header>
        <S.SessionsContent data-testid="sessionsContent">
          {sessionsList?.length > 0 && <SessionsTable data={sessionsList} />}
        </S.SessionsContent>
      </S.SessionsSection>
    );
  };

  const onVerifyUserInfoCriticality = () => {
    getUserInfo();
  };

  return (
    <S.UserInfoContainer>
      <HeaderUserInfo user={userInfo} />
      <S.Divisor />
      <S.TabsContainer data-testid="tabsContainer">
        <TabList selectedIndex={1} />
        <BasicInfo />
        <PatientRecords userCognitoId={userInfo?.cognitoID} />
        {isPatientNotesEnabled && (
          <NotesSection isLoading={getUserInfoRequest.isLoading()} />
        )}

        <CriticalUserSection
          data-testid="criticalUserSection"
          verifyUserInfoCriticality={onVerifyUserInfoCriticality}
          role={specialistType}
          criticality={{
            hasShownCriticalSigns:
              userInfo?.criticality?.has_shown_critical_signs,
            isCriticalityConfirmed:
              userInfo?.criticality?.is_criticality_confirmed,
            confirmedAt: userInfo?.criticality?.confirmed_at,
            confirmedWithExtUserPresent:
              userInfo?.criticality?.confirmed_with_ext_user_present || false,
            hasExtUsers:
              userInfo?.company_ext_referral_info?.has_ext_users || false,
          }}
          cognitoId={userInfo?.cognitoID}
        />
        <SessionsInfo />
      </S.TabsContainer>
    </S.UserInfoContainer>
  );
};

export default withAuth(EndUser);
